import { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import Box from '@mui/material/Box'
import { yupResolver } from '@hookform/resolvers/yup'

import { TextFieldController } from '../../../components/forms/fields/TextFieldController'
import { SalaryItemDto } from '../../../service/types'
import { SalaryItemFormValues, useSalaryItemSchema } from '../schema'

type Props = {
  open: boolean
  onClose: () => void
  onSave: (values: SalaryItemFormValues) => void
  salaryItem: SalaryItemDto | null
}

export const SalaryItemDialog: FC<Props> = ({ open, onClose, onSave, salaryItem }) => {
  const { t } = useTranslation()
  const schema = useSalaryItemSchema()

  const { control, handleSubmit, reset } = useForm<SalaryItemFormValues>({
    resolver: yupResolver(schema),
    defaultValues: schema.getDefault(),
  })

  useEffect(() => {
    if (open) {
      reset(salaryItem ? { description: salaryItem.description, amount: salaryItem.amount } : schema.getDefault())
    }
  }, [open, salaryItem, reset]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (values: SalaryItemFormValues) => {
    onSave(values)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {salaryItem ? t('instructorSalary.editSalaryItem') : t('instructorSalary.addSalaryItem')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextFieldController name="description" control={control} label={t('instructorSalary.form.description')} />
            <TextFieldController
              name="amount"
              control={control}
              label={t('instructorSalary.form.amount')}
              type="number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('general.button.cancel')}</Button>
          <Button type="submit" variant="contained">
            {t('general.button.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
